import React, { createContext, useContext, useEffect, useCallback, useMemo, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, AuthResponse, AuthError, Provider } from '@supabase/supabase-js';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signInWithGoogle: () => Promise<{ data: any; error: any }>;
  signInWithApple: () => Promise<{ data: any; error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  isConfigured: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string, username: string) => {
    if (!supabase) {
      return { data: null, error: new Error('Authentication is not configured.') };
    }

    const trimmedUsername = username?.trim();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: trimmedUsername ? { username: trimmedUsername } : {},
      },
    });

    if (!error && data.user && trimmedUsername) {
      const { error: profileError } = await supabase.from('user_profiles').upsert({
        id: data.user.id,
        username: trimmedUsername,
        display_name: trimmedUsername,
      });

      if (profileError) {
        console.error('Failed to create user profile:', profileError);
        return { data: null, error: new Error('Failed to create user profile. Please try again.') };
      }
    }

    return { data, error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) {
      return { data: { user: null, session: null }, error: { message: 'Authentication is not configured.', status: 400 } as any };
    }
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signInWithProvider = useCallback(async (provider: Provider) => {
    if (!supabase) {
      return { data: null, error: new Error('Authentication is not configured.') };
    }

    return supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
  }, []);

  const signInWithGoogle = useCallback(async () => signInWithProvider('google'), [signInWithProvider]);
  const signInWithApple = useCallback(async () => signInWithProvider('apple'), [signInWithProvider]);

  const resetPassword = useCallback(async (email: string) => {
    if (!supabase) {
      return { data: null, error: new Error('Authentication is not configured.') };
    }

    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) {
      return { error: null };
    }

    return supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signInWithApple,
      resetPassword,
      signOut,
      isConfigured: Boolean(supabase),
    }),
    [user, loading, signUp, signIn, signInWithGoogle, signInWithApple, resetPassword, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
